<template>
	<div class="AccountingExports">
		<vx-card class="mb-6">
			<h3 class="mb-4">{{ $t('accountingExportPeriod') }}</h3>
			<div class="filter vx-row items-end">
				<vx-input-group class="vx-col py-2">
					<vs-datepicker
						v-model="dateFrom"
						:placeholder="$t('from')"
						:disabled="isLoading"
						format="dd.MM.yyyy"
						name="dateFrom"
					/>
				</vx-input-group>

				<vx-input-group class="vx-col py-2">
					<vs-datepicker
						v-model="dateTo"
						:placeholder="$t('to')"
						:disabled="isLoading"
						format="dd.MM.yyyy"
						name="dateTo"
					/>
				</vx-input-group>

				<div class="vx-col py-2">
					<vs-custom-button
						color="dark"
						type="border"
						:disabled="isLoading"
						@click="resetDates"
					>
						{{ $t('reset') }}
					</vs-custom-button>
				</div>
			</div>
		</vx-card>

		<vx-card
			v-for="exportItem in exports"
			:key="exportItem.key"
			class="mb-6"
		>
			<h2 class="mb-2">{{ $t(exportItem.titleKey) }}</h2>
			<p class="mb-6 text-grey">{{ $t(exportItem.descriptionKey) }}</p>

			<vs-custom-button
				color="primary"
				:disabled="!canDownload || isLoading"
				@click="downloadExcel(exportItem)"
			>
				{{ $t('accountingExportDownloadExcel') }}
			</vs-custom-button>
		</vx-card>
	</div>
</template>

<script>
import moment from 'moment'
import notifier from '@/mixins/notifier'

export default {
	name: 'AccountingExports',
	mixins: [notifier],
	data() {
		return {
			dateFrom: '',
			dateTo: '',
			isLoading: false,
			exports: [
				{
					key: 'total_revenue',
					endpoint: '/accountant/accounting-export/download-total-revenue',
					filenamePrefix: 'total_revenue',
					titleKey: 'accountingExportTotalRevenueTitle',
					descriptionKey: 'accountingExportTotalRevenueDescription',
				},
				{
					key: 'total_clients_delivered',
					endpoint: '/accountant/accounting-export/download-total-clients-delivered',
					filenamePrefix: 'total_clients_delivered',
					titleKey: 'accountingExportTotalClientsDeliveredTitle',
					descriptionKey: 'accountingExportTotalClientsDeliveredDescription',
				},
				{
					key: 'new_clients_paid',
					endpoint: '/accountant/accounting-export/download-new-clients-paid',
					filenamePrefix: 'new_clients_paid',
					titleKey: 'accountingExportNewClientsPaidTitle',
					descriptionKey: 'accountingExportNewClientsPaidDescription',
				},
				{
					key: 'total_clients_paid',
					endpoint: '/accountant/accounting-export/download-total-clients-paid',
					filenamePrefix: 'total_clients_paid',
					titleKey: 'accountingExportTotalClientsPaidTitle',
					descriptionKey: 'accountingExportTotalClientsPaidDescription',
				},
				{
					key: 'new_client_orders_paid',
					endpoint: '/accountant/accounting-export/download-new-client-orders-paid',
					filenamePrefix: 'new_client_orders_paid',
					titleKey: 'accountingExportNewClientOrdersPaidTitle',
					descriptionKey: 'accountingExportNewClientOrdersPaidDescription',
				},
				{
					key: 'new_client_orders_unpaid',
					endpoint: '/accountant/accounting-export/download-new-client-orders-unpaid',
					filenamePrefix: 'new_client_orders_unpaid',
					titleKey: 'accountingExportNewClientOrdersUnpaidTitle',
					descriptionKey: 'accountingExportNewClientOrdersUnpaidDescription',
				},
				{
					key: 'author_payments_completed',
					endpoint: '/accountant/accounting-export/download-author-payments-completed',
					filenamePrefix: 'author_payments_completed',
					titleKey: 'accountingExportAuthorPaymentsCompletedTitle',
					descriptionKey: 'accountingExportAuthorPaymentsCompletedDescription',
				},
				{
					key: 'pending_payment_milestones',
					endpoint: '/accountant/accounting-export/download-pending-payment-milestones',
					filenamePrefix: 'pending_payment_milestones',
					titleKey: 'accountingExportPendingPaymentMilestonesTitle',
					descriptionKey: 'accountingExportPendingPaymentMilestonesDescription',
				},
			],
		}
	},
	computed: {
		canDownload() {
			return Boolean(this.dateFrom && this.dateTo)
		},
	},
	methods: {
		resetDates() {
			this.dateFrom = ''
			this.dateTo = ''
		},
		buildFilterQuery() {
			return [
				`filter[date_from]=${moment(this.dateFrom).format('YYYY-MM-DD')}`,
				`filter[date_to]=${moment(this.dateTo).format('YYYY-MM-DD')}`,
			].join('&')
		},
		async downloadExcel(exportItem) {
			if (!this.canDownload || this.$store.state.auth.role !== 'accountant') {
				return
			}

			this.isLoading = true

			try {
				const response = await this.$http.get(
					`${exportItem.endpoint}?${this.buildFilterQuery()}`,
					{
						headers: {
							Authorization: 'Bearer ' + this.$store.state.auth.token,
						},
						responseType: 'blob',
					}
				)

				const blob = response.data
				const contentType = response.headers['content-type'] || ''

				if (contentType.includes('application/json')) {
					const text = await blob.text()
					const payload = JSON.parse(text)
					throw new Error(payload.message || this.$t('fileDownloadError'))
				}

				const dateFrom = moment(this.dateFrom).format('YYYY-MM-DD')
				const dateTo = moment(this.dateTo).format('YYYY-MM-DD')
				const url = window.URL.createObjectURL(blob)
				const link = document.createElement('a')
				link.href = url
				link.download = `${exportItem.filenamePrefix}_${dateFrom}_${dateTo}.xlsx`
				document.body.appendChild(link)
				link.click()
				document.body.removeChild(link)
				window.URL.revokeObjectURL(url)
			} catch (error) {
				this.notifyError(error.message || this.$t('fileDownloadError'))
			} finally {
				this.isLoading = false
			}
		},
	},
}
</script>

<style scoped>
.text-grey {
	color: #626262;
}
</style>
